import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
    createSession,
    getSession,
    updateSession,
    deleteSession,
    getAllSessions
} from './sessionApi';

// Get all sessions
export const useGetAllSessions = () => {
    return useQuery({
        queryKey: ['sessions'],
        queryFn: async () => {
            try {
                const data = await getAllSessions();
                return data?.sessions || data || [];
            } catch (error) {
                console.error('Error in useGetAllSessions:', error);
                toast.error('Failed to fetch sessions');
                throw error;
            }
        },
    });
};

// Get a single session by ID
export const useGetSession = (sessionId) => {
    return useQuery({
        queryKey: ['session', sessionId],
        queryFn: () => getSession(sessionId),
        enabled: !!sessionId,
    });
};

// Create a new session
export const useCreateSession = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data) => {
            // Ensure we only pass the exact fields needed
            const createData = {
                title: data.title.trim(),
                startDate: data.startDate,
                endDate: data.endDate
            };
            
            try {
                console.log('Attempting to create session with:', createData);
                const result = await createSession(createData);
                console.log('Session creation successful:', result);
                return result;
            } catch (error) {
                // Log the error with full details
                console.error('Session creation failed:', {
                    inputData: createData,
                    error: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    stack: error.stack
                });

                // Rethrow with a more specific error message
                if (error.message.includes('DepartmentWhereUniqueInput')) {
                    throw new Error('Session creation failed: Internal server configuration issue. Please contact support.');
                }
                throw error;
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['sessions'] });
            toast.success('Session created successfully');
        },
        onError: (error) => {
            console.error('Session creation error:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            // More specific error messages based on the error type
            if (error.message.includes('Internal server configuration')) {
                toast.error(error.message);
            } else if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('Failed to create session. Please try again.');
            }
        },
        retry: false // Disable retries since this might be a schema issue
    });
};

// Update an existing session
export const useUpdateSession = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ sessionId, data }) => {
            // Enhanced input validation
            if (!sessionId || typeof sessionId !== 'string') {
                throw new Error('Valid session ID string is required for update');
            }

            if (!data || typeof data !== 'object') {
                throw new Error('Valid session data object is required');
            }

            // Log mutation attempt
            console.log('Update mutation called with:', { sessionId, data });

            const result = await updateSession({ sessionId, data });
            
            // Log successful result
            console.log('Update mutation succeeded:', result);
            
            return result;
        },
        onSuccess: (data, variables) => {
            // Invalidate both the individual session and the sessions list
            queryClient.invalidateQueries({ queryKey: ['session', variables.sessionId] });
            queryClient.invalidateQueries({ queryKey: ['sessions'] });
            // Log cache invalidation
            console.log('Cache invalidated for session:', variables.sessionId);
        },
        onError: (error) => {
            // Enhanced error logging
            console.error('Session update mutation error:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            throw error; // Rethrow to allow component-level handling
        }
    });
};

// Delete a session
export const useDeleteSession = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteSession,
        onSuccess: (data, sessionId) => {
            queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
            queryClient.invalidateQueries({ queryKey: ['sessions'] });
            toast.success('Session deleted successfully');
        },
        onError: (error) => {
            console.error('Error deleting session:', error);
            toast.error(error.response?.data?.message || 'Failed to delete session');
        }
    });
};